import { useCallback, useEffect, useRef, useState } from 'react';
import { startVoiceLoop } from '../lib/vad';
import { streamTurn, transcribeClip } from '../lib/chatStream';
import { endsWithSendWord, stripSendWord } from '../lib/sendWord';
import useSpeechOutput from './useSpeechOutput';

/**
 * The engine behind both hands-free chat modes. One always-open mic, gated by
 * the RMS meter in lib/vad; the modes differ only in who ends your turn.
 *
 *   conversation — a clip IS a turn. A long silence ends it, the reply is
 *     spoken, and the mic reopens. The app decides you're done.
 *   walkie — silence only closes one segment, which is transcribed in the
 *     background while you keep talking (that hides most of the STT wait).
 *     The turn ends when the running transcript ends with the send word, or
 *     you tap Send. YOU decide you're done; it can never cut you off.
 *
 * Interrupting the reply works in both: the Stop button always, and — when
 * barge-in is switched on — talking over it. Barge-in is opt-in because the
 * mic demonstrably hears the phone speaker (measured on Android Chrome), so
 * an open mic during playback can transcribe the app's own voice. It is much
 * safer on headphones, which is what the UI says.
 *
 * Turns go through the ordinary chat endpoint, so the transcript on screen is
 * the real conversation, not a side channel.
 */

const POLICY = {
  // 3s of silence: thinking pauses mid-thought are commonly 1-2s, and cutting
  // someone off mid-idea is the worst failure this mode can have.
  conversation: { silenceHangMs: 3000, maxUtteranceMs: 30000 },
  // Short hang so the send word is caught quickly, and an 8s cap so a send
  // word can't stay buried inside a segment that noise keeps open.
  walkie: { silenceHangMs: 1200, maxUtteranceMs: 8000 },
};

/** Segments transcribed at once in walkie mode; results still append in order. */
const MAX_PARALLEL_STT = 2;

export default function useVoiceSession({
  conversationId,
  mode,
  bargeIn = false,
  sendWord,
  onNotice,
}) {
  const [status, setStatus] = useState('connecting');
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState('');
  const [transcribing, setTranscribing] = useState(0);
  const [errorText, setErrorText] = useState('');
  const [level, setLevel] = useState(0);
  const [threshold, setThreshold] = useState(0.018);

  const speech = useSpeechOutput();
  const { begin, push, end, cancel: cancelSpeech } = speech;

  const loopRef = useRef(null);
  const deadRef = useRef(false);
  const turnActiveRef = useRef(false);
  // Bumped whenever a turn starts or is interrupted; an in-flight turn that
  // finds its id stale must not speak or reopen the mic.
  const turnIdRef = useRef(0);
  const pendingRef = useRef('');
  const finalizeRef = useRef(false);
  const wakeLockRef = useRef(null);
  const lastLevelRef = useRef(0);
  // Lets Stop kill a reply that is still being generated, not just spoken.
  const abortRef = useRef(null);

  // Walkie STT pipeline: each closed segment takes a sequence number, up to
  // MAX_PARALLEL_STT transcribe concurrently, and results append in order.
  const waitingRef = useRef([]);
  const resultsRef = useRef(new Map());
  const inFlightRef = useRef(0);
  const nextSeqRef = useRef(0);
  const nextAppendRef = useRef(0);

  // Live mirrors so the poll/recorder closures never read stale props.
  const modeRef = useRef(mode);
  const bargeRef = useRef(bargeIn);
  const sendWordRef = useRef(sendWord);
  const convRef = useRef(conversationId);
  const noticeRef = useRef(onNotice);
  // Indirection so the mic loop (booted once) always calls the latest handlers.
  const handlersRef = useRef({});

  useEffect(() => {
    bargeRef.current = bargeIn;
  }, [bargeIn]);
  useEffect(() => {
    sendWordRef.current = sendWord;
  }, [sendWord]);
  useEffect(() => {
    convRef.current = conversationId;
  }, [conversationId]);
  useEffect(() => {
    noticeRef.current = onNotice;
  }, [onNotice]);

  const notify = useCallback((msg) => noticeRef.current?.(msg), []);

  const setPendingText = useCallback((text) => {
    pendingRef.current = text;
    setPending(text);
  }, []);

  const pushMessage = useCallback((msg) => {
    const id = `${msg.role}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setMessages((prev) => [...prev, { ...msg, id }]);
    return id;
  }, []);

  const clearPipeline = useCallback(() => {
    waitingRef.current = [];
    resultsRef.current.clear();
    nextSeqRef.current = 0;
    nextAppendRef.current = 0;
    inFlightRef.current = 0;
    finalizeRef.current = false;
    setTranscribing(0);
  }, []);

  /** Hand the floor back to the user. */
  const resumeListening = useCallback(() => {
    if (deadRef.current) return;
    turnActiveRef.current = false;
    clearPipeline();
    setPendingText('');
    loopRef.current?.setState('listen');
    setStatus('listening');
  }, [clearPipeline, setPendingText]);

  /** Stop the reply — mid-thought or mid-sentence — and take the floor back. */
  const interrupt = useCallback(() => {
    turnIdRef.current += 1; // orphan any in-flight turn's speech/resume
    abortRef.current?.abort();
    abortRef.current = null;
    cancelSpeech();
    resumeListening();
  }, [cancelSpeech, resumeListening]);

  /** Send one turn: stream the reply, speaking each sentence as it lands. */
  const runTurn = useCallback(
    async (text) => {
      turnIdRef.current += 1;
      const turn = turnIdRef.current;
      turnActiveRef.current = true;
      loopRef.current?.setState('idle');
      clearPipeline();
      setPendingText('');
      pushMessage({ role: 'user', text });
      setStatus('thinking');

      const replyId = pushMessage({ role: 'assistant', text: '' });
      const controller = new AbortController();
      abortRef.current = controller;
      begin();
      let spoke = false;

      try {
        await streamTurn(convRef.current, text, {
          signal: controller.signal,
          onChunk: (delta) => {
            if (turn !== turnIdRef.current || deadRef.current) return;
            setMessages((prev) =>
              prev.map((m) => (m.id === replyId ? { ...m, text: m.text + delta } : m))
            );
            push(delta);
            if (!spoke) {
              spoke = true;
              // Audio starts on the first complete sentence, so the mic can
              // already be watching for a barge-in.
              setStatus('speaking');
              loopRef.current?.setState(bargeRef.current ? 'bargein' : 'idle');
            }
          },
        });
      } catch (err) {
        // An interrupt already reset everything — its abort landing here is
        // the expected path, not a failure to report.
        if (turn !== turnIdRef.current || deadRef.current) return;
        cancelSpeech();
        setMessages((prev) => prev.filter((m) => m.id !== replyId || m.text));
        const s = err?.response?.status;
        if (s === 429) notify('Slow down a moment — too many turns.');
        else if (err?.message === 'Session expired') notify('Session expired — sign in again.');
        else notify("That didn't get through. Try again.");
        resumeListening();
        return;
      }

      if (turn !== turnIdRef.current || deadRef.current) return;
      abortRef.current = null;
      // The mic reopens from onEnd, never before — half duplex.
      end({
        onEnd: () => {
          if (turn !== turnIdRef.current) return;
          resumeListening();
        },
      });
    },
    [begin, push, end, cancelSpeech, clearPipeline, notify, pushMessage, resumeListening, setPendingText]
  );

  /** Conversation mode: the clip is the whole turn. */
  const handleConversationClip = useCallback(
    async (blob) => {
      turnActiveRef.current = true;
      loopRef.current?.setState('idle');
      setStatus('thinking');
      let text = '';
      try {
        text = await transcribeClip(blob);
      } catch (err) {
        if (err?.response?.status !== 400) notify("I didn't catch that — say it again?");
      }
      if (deadRef.current) return;
      if (!text) {
        resumeListening();
        return;
      }
      await runTurn(text);
    },
    [notify, resumeListening, runTurn]
  );

  /** Walkie: finalize once every closed segment has been transcribed. */
  const maybeFinalize = useCallback(() => {
    if (
      !finalizeRef.current ||
      nextAppendRef.current < nextSeqRef.current ||
      turnActiveRef.current ||
      deadRef.current
    ) {
      return;
    }
    finalizeRef.current = false;
    const text = stripSendWord(pendingRef.current, sendWordRef.current);
    if (!text) {
      // The send word with nothing in front of it — keep the floor.
      setPendingText('');
      setStatus('listening');
      return;
    }
    runTurn(text);
  }, [runTurn, setPendingText]);

  /** Append every contiguous finished segment, in the order it was spoken. */
  const drainResults = useCallback(() => {
    while (resultsRef.current.has(nextAppendRef.current)) {
      const text = resultsRef.current.get(nextAppendRef.current);
      resultsRef.current.delete(nextAppendRef.current);
      nextAppendRef.current += 1;
      if (text && !turnActiveRef.current) {
        const next = pendingRef.current ? `${pendingRef.current} ${text}` : text;
        setPendingText(next);
        if (endsWithSendWord(next, sendWordRef.current)) finalizeRef.current = true;
      }
    }
    setTranscribing(Math.max(0, nextSeqRef.current - nextAppendRef.current));
    maybeFinalize();
  }, [maybeFinalize, setPendingText]);

  const pumpTranscription = useCallback(() => {
    while (
      inFlightRef.current < MAX_PARALLEL_STT &&
      waitingRef.current.length > 0 &&
      !deadRef.current
    ) {
      const { seq, blob } = waitingRef.current.shift();
      inFlightRef.current += 1;
      transcribeClip(blob)
        .then((text) => resultsRef.current.set(seq, text))
        .catch(() => {
          resultsRef.current.set(seq, '');
          notify('One bit failed to transcribe — say it again before you send.');
        })
        .finally(() => {
          inFlightRef.current -= 1;
          drainResults();
          pumpTranscription();
        });
    }
  }, [drainResults, notify]);

  // Latest handlers for the once-booted mic loop.
  handlersRef.current.onClip = (blob) => {
    if (deadRef.current || turnActiveRef.current) return;
    if (modeRef.current === 'conversation') {
      handleConversationClip(blob);
      return;
    }
    setStatus((s) => (s === 'hearing' ? 'listening' : s));
    waitingRef.current.push({ seq: nextSeqRef.current, blob });
    nextSeqRef.current += 1;
    setTranscribing(Math.max(0, nextSeqRef.current - nextAppendRef.current));
    pumpTranscription();
  };
  handlersRef.current.onBargeIn = () => {
    if (deadRef.current) return;
    interrupt();
  };

  /** Walkie's Send button: flush the open segment, then ship once it lands. */
  const send = useCallback(() => {
    if (turnActiveRef.current || deadRef.current) return;
    finalizeRef.current = true;
    const loop = loopRef.current;
    if (loop) {
      loop.flush().then(() => {
        // The flushed clip (if any) arrives via onClip before this resolves;
        // if everything was already appended, this finalizes right away.
        drainResults();
        maybeFinalize();
      });
    } else {
      maybeFinalize();
    }
  }, [drainResults, maybeFinalize]);

  // Mode changes without dropping the mic: swap turn-ending policy, and throw
  // away a half-built walkie turn (it means nothing in conversation mode).
  useEffect(() => {
    modeRef.current = mode;
    loopRef.current?.setPolicy(POLICY[mode] || POLICY.conversation);
    if (!turnActiveRef.current) {
      clearPipeline();
      setPendingText('');
    }
  }, [mode, clearPipeline, setPendingText]);

  // If barge-in is switched off mid-reply, close the mic straight away.
  useEffect(() => {
    if (turnActiveRef.current && status === 'speaking') {
      loopRef.current?.setState(bargeIn ? 'bargein' : 'idle');
    }
  }, [bargeIn, status]);

  // Boot the mic once; full teardown on unmount.
  useEffect(() => {
    deadRef.current = false;
    let live = null;

    startVoiceLoop(POLICY[modeRef.current] || POLICY.conversation, {
      onClip: (blob) => handlersRef.current.onClip?.(blob),
      onBargeIn: () => handlersRef.current.onBargeIn?.(),
      onSpeechStart: () => setStatus((s) => (s === 'listening' ? 'hearing' : s)),
      onSpeechEnd: () => setStatus((s) => (s === 'hearing' ? 'listening' : s)),
      onLevel: (rms, thr) => {
        // Repainting 20x/sec for a meter is wasteful; 5x/sec reads the same.
        const now = Date.now();
        if (now - lastLevelRef.current < 200) return;
        lastLevelRef.current = now;
        setLevel(rms);
        setThreshold(thr);
      },
    })
      .then((loop) => {
        if (deadRef.current) {
          loop.close();
          return;
        }
        live = loop;
        loopRef.current = loop;
        setStatus('listening');
      })
      .catch((e) => {
        if (deadRef.current) return;
        setStatus('error');
        const denied = e?.name === 'NotAllowedError' || e?.name === 'SecurityError';
        setErrorText(
          denied
            ? 'Microphone access was denied. Allow it in your browser to talk.'
            : "Couldn't reach your microphone. You can type instead."
        );
      });

    return () => {
      deadRef.current = true;
      turnIdRef.current += 1;
      abortRef.current?.abort();
      abortRef.current = null;
      live?.close();
      loopRef.current = null;
      try {
        window.speechSynthesis?.cancel();
      } catch {
        /* nothing queued */
      }
    };
  }, []);

  // Keep the screen awake — a phone on a desk mid-conversation shouldn't sleep.
  useEffect(() => {
    const acquire = () => {
      if (!('wakeLock' in navigator) || deadRef.current) return;
      navigator.wakeLock
        .request('screen')
        .then((sentinel) => {
          sentinel.addEventListener?.('release', () => {
            wakeLockRef.current = null;
          });
          wakeLockRef.current = sentinel;
        })
        .catch(() => {
          /* not critical — the screen may just dim */
        });
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) acquire();
    };
    acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      try {
        wakeLockRef.current?.release();
      } catch {
        /* already gone */
      }
      wakeLockRef.current = null;
    };
  }, []);

  return {
    status,
    messages,
    pending,
    transcribing,
    errorText,
    level,
    threshold,
    speech,
    send,
    interrupt,
    // True when a Send tap would actually ship something: text already
    // transcribed, a segment still in STT, or words being spoken right now.
    canSend: pending.trim().length > 0 || transcribing > 0 || status === 'hearing',
  };
}
